// Shared validation for ORT scan-result uploads (ZIP, standalone JSON or SPDX YAML), used
// by both the new-scan wizard (NewScan.tsx) and the existing-scan re-upload dialog
// (UploadOrtFiles.tsx) so the two entry points can't drift out of sync.

export const SCAN_RESULT_ACCEPT = '.zip,.json,.yml,.yaml';

function isYamlFileName(fileName: string): boolean {
  const name = fileName.toLowerCase();
  return name.endsWith('.yml') || name.endsWith('.yaml');
}

export function isAcceptedScanResultFile(fileName: string): boolean {
  const name = fileName.toLowerCase();
  return name.endsWith('.zip') || name.endsWith('.json') || isYamlFileName(name);
}

export async function validateOrtZipFile(file: File): Promise<string | null> {
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(file);
    const matches = zip.file(/evaluation-result\.json$/);
    if (!matches[0]) return 'ZIP must contain evaluation-result.json';
    return null;
  } catch (e) {
    return 'Invalid ZIP: ' + (e instanceof Error ? e.message : 'parse error');
  }
}

// Accepts a CycloneDX SBOM ("bomFormat": "CycloneDX"), an SPDX 2.x document
// ("spdxVersion": "SPDX-2.x") or a standalone ORT-native evaluation-result.json (has an
// analyzer.result section) — matching exactly what the backend's OrtNativeAdapter/
// CycloneDxAdapter/SpdxAdapter will accept, so this check never rejects a file the
// server would otherwise process.
export async function validateScanResultJsonFile(file: File): Promise<string | null> {
  try {
    const parsed = JSON.parse(await file.text());
    const isCycloneDx = parsed?.bomFormat === 'CycloneDX';
    const isSpdx = typeof parsed?.spdxVersion === 'string' && parsed.spdxVersion.startsWith('SPDX-2');
    const isOrtNative = !!parsed?.analyzer?.result;
    if (!isCycloneDx && !isSpdx && !isOrtNative) {
      return 'JSON file must be a CycloneDX SBOM ("bomFormat": "CycloneDX"), an SPDX 2.x document ("spdxVersion": "SPDX-2.x") or an ORT evaluation-result.json (with an "analyzer.result" section)';
    }
    return null;
  } catch (e) {
    return 'Invalid JSON: ' + (e instanceof Error ? e.message : 'parse error');
  }
}

// YAML is only accepted for SPDX. A lightweight top-level key check (rather than a full
// YAML parse) avoids pulling a YAML parser into the client; the server parses it fully.
export async function validateScanResultYamlFile(file: File): Promise<string | null> {
  const text = await file.text();
  if (!/^\s*spdxVersion\s*:\s*["']?SPDX-2/m.test(text)) {
    return 'YAML file must be an SPDX 2.x document (with a top-level "spdxVersion: SPDX-2.x")';
  }
  return null;
}

export async function validateScanResultFile(file: File): Promise<string | null> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.json')) return validateScanResultJsonFile(file);
  if (isYamlFileName(name)) return validateScanResultYamlFile(file);
  return validateOrtZipFile(file);
}

// Tag for which upload format a scan's results came from (scans.resultformat). Returns
// null for scans whose results haven't been processed yet, so callers can filter it out.
const RESULT_FORMAT_BADGES: Record<string, { label: string; className: string }> = {
  ort: {
    label: 'ORT zip',
    className: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700',
  },
  cyclonedx: {
    label: 'CycloneDX',
    className: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700',
  },
  spdx: {
    label: 'SPDX',
    className: 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/40 dark:text-teal-300 dark:border-teal-700',
  },
};

export function getResultFormatBadge(resultformat?: string | null): { label: string; className: string } | null {
  return (resultformat && RESULT_FORMAT_BADGES[resultformat]) || null;
}
